import { Component, Input, OnChanges, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { CallState } from '@lcs/shared/api';
import { Store, select } from '@ngrx/store';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { getCurrentUserIsSystemUser } from 'src/app/authentication/state/authentication.selector';
import { TwoColumnLayoutService } from 'src/app/common-components/column-layouts/two-column-list-layout/services/selected-id.service';
import { Detail } from 'src/app/common-components/detail/detail';
import { FormsBase } from 'src/app/common-components/forms-base/forms-base';
import { getDepartmentPrivileges } from 'src/app/privileges/privileges.selector';
import { DepartmentPrivileges } from '../../department-register/models/department-privileges';
import { DepartmentDetailSaveEvent } from '../models/department-detail-form-submit-event';
import { DepartmentDetailView } from '../models/department-detail-view.model';
import { DepartmentDetailFormService } from '../services/department-detail-form.service';
import * as DepartmentDetailActions from '../state/department-detail.actions';
import { getDepartment, getDepartmentCallState } from '../state/department-detail.selectors';

@Component({
    encapsulation: ViewEncapsulation.None,
    host: { class: 'entity-detail' },
    providers: [DepartmentDetailFormService, { provide: FormsBase, useExisting: DepartmentDetailFormService }],
    selector: 'ndt-department-detail',
    templateUrl: './department-detail.component.html',
    standalone: false
})
export class DepartmentDetailComponent
   extends Detail<DepartmentDetailFormService>
   implements OnInit, OnDestroy, OnChanges
{
   @Input() departmentId: number;

   department$: Observable<DepartmentDetailView | null>;

   callState$: Observable<CallState>;

   departmentPrivileges$: Observable<DepartmentPrivileges>;

   selectedDepartmentId: number;

   constructor(
      public store: Store,
      private router: Router,
      private twoColumnLayoutService: TwoColumnLayoutService,
      protected formService: DepartmentDetailFormService
   ) {
      super(store, formService);
      this.department$ = this.store.pipe(select(getDepartment));
      this.callState$ = this.store.pipe(select(getDepartmentCallState));
      this.departmentPrivileges$ = this.store.pipe(select(getDepartmentPrivileges));
      this.isSystemUser$ = this.store.pipe(select(getCurrentUserIsSystemUser));
      this.store.dispatch(DepartmentDetailActions.departmentDetailLoad());
   }

   ngOnInit(): void {
      this.router.events.pipe(takeUntil(this.destroyed$)).subscribe((e) => {
         if (e instanceof NavigationEnd) {
            this.store.dispatch(DepartmentDetailActions.departmentDetailLoad());
         }
      });
      this.twoColumnLayoutService.selectedId$.pipe(takeUntil(this.destroyed$)).subscribe((id) => {
         if (id !== null) {
            this.selectedDepartmentId = id;
            this.store.dispatch(DepartmentDetailActions.updateDepartmentId({ departmentId: id }));
         }
      });
   }

   ngOnChanges(): void {
      this.store.dispatch(DepartmentDetailActions.departmentDetailLoad());
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   onFormSaveCanceled(): void {
      this.store.dispatch(DepartmentDetailActions.departmentDetailCancel());
   }

   onFormSubmitted(event: DepartmentDetailSaveEvent): void {
      this.store.dispatch(DepartmentDetailActions.saveDepartmentDetail(event));
   }

   onDeleteDepartment(event: { departmentId: number; name: string | null }) {
      this.store.dispatch(DepartmentDetailActions.deleteDepartment(event));
   }

   onRenameDepartment(event: { departmentId: number; name: string | null }) {
      this.store.dispatch(
         DepartmentDetailActions.renameDepartment({ departmentId: event.departmentId, name: event.name })
      );
   }

   onDeactivateDepartment(event: { departmentId: number; name: string | null; isActive: boolean }) {
      this.store.dispatch(DepartmentDetailActions.deactivateDepartment({ department: event }));
   }

   onMergeDepartment(event: { departmentId: number; name: string | null; accountId: number }) {
      this.store.dispatch(
         DepartmentDetailActions.mergeDepartment({
            departmentId: event.departmentId,
            name: event.name,
            accountId: event.accountId,
         })
      );
   }
   onUntagDepartment(event: { departmentId: number; name: string }) {
      this.store.dispatch(
         DepartmentDetailActions.untagDepartment({ departmentId: event.departmentId, name: event.name })
      );
   }
}
